import { useQuery } from '@tanstack/react-query';

import { hubFetch } from '@/api/client';
import type { AiToolsDashboard } from '@/api/types';
import { useEnvStore } from '@/env/envStore';

/** Window options (days) for the AI-tools dashboard. Backend accepts 7–31. */
export const AI_TOOLS_WINDOW_OPTIONS = [7, 14, 30] as const;
export type AiToolsWindow = (typeof AI_TOOLS_WINDOW_OPTIONS)[number];
export const DEFAULT_AI_TOOLS_WINDOW: AiToolsWindow = 30;

/**
 * Fetch the developer-AI tool spend dashboard (Claude + Copilot). Corp-only:
 * the query is disabled outside the Corp environment so non-Corp pages never
 * hit the endpoint (which 400s for other envs). The backend caches per
 * (env, days) for ~1h, so a 5-minute client stale-time avoids re-fetching on
 * back-to-back mounts.
 */
export function useAiTools(days: AiToolsWindow) {
  const env = useEnvStore((s) => s.env);
  return useQuery({
    queryKey: ['ai-tools', env, days],
    queryFn: (): Promise<AiToolsDashboard> =>
      hubFetch<AiToolsDashboard>('/api/ai-tools', {
        searchParams: { days },
      }),
    enabled: env === 'corp',
    staleTime: 5 * 60_000,
    gcTime: 15 * 60_000,
  });
}
